import React, { useState } from "react";
import { ArrowRightOutlined, CalendarOutlined, SwapOutlined } from "@ant-design/icons";
import { Popover, DatePicker, TimePicker, Button } from "antd";
import type { Moment } from "moment";
import moment from "moment";
import "./customDatePicker.css";
import { useSelector, useDispatch } from "react-redux";

const { RangePicker } = DatePicker;

const CustomRangePicker: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [range, setRange] = useState<[Moment, Moment] | null>([
    moment().startOf("month"),
    moment().endOf("month")
  ]);
  const { startDate, endDate } = useSelector((state: any) => state.ui.feedbackFilter);
  const [startTime, setStartTime] = useState<Moment>(moment("00:00", "HH:mm"));
  const [endTime, setEndTime] = useState<Moment>(moment("23:59", "HH:mm"));
  const dispatch = useDispatch();

  React.useEffect(() => {
    const start = moment(startDate);
    const end = moment(endDate);
    const today = moment();

    if (start.isSame(today.clone().startOf("month"), "day") && end.isSame(today.clone().endOf("month"), "day")) {
      setSelectedOption("This Month");
      setRange([start, end]);
    } else if (start.isSame(today.clone().subtract(1, "month").startOf("month"), "day") &&
      end.isSame(today.clone().subtract(1, "month").endOf("month"), "day")) {
      setSelectedOption("Last Month");
      setRange([start, end]);
    } else if (start.isSame(today.clone().startOf("week"), "day") && end.isSame(today.clone().endOf("week"), "day")) {
      setSelectedOption("This Week");
      setRange([start, end]);
    } else if (start.isSame(today.clone().subtract(1, "week").startOf("week"), "day") &&
      end.isSame(today.clone().subtract(1, "week").endOf("week"), "day")) {
      setSelectedOption("Last Week");
      setRange([start, end]);
    } else {
      setSelectedOption("Custom");
      setRange([start, end]);
    }
  }, [startDate, endDate]);

  const handleOptionClick = (option: string) => {
    setSelectedOption(option);
    const today = moment();
    switch (option) {
      case "This Month":
        setRange([today.clone().startOf("month"), today.clone().endOf("month")]);
        break;
      case "Last Month": {
        const lastMonth = today.clone().subtract(1, "month");
        setRange([lastMonth.clone().startOf("month"), lastMonth.clone().endOf("month")]);
        break;
      }
      case "This Week":
        setRange([today.clone().startOf("week"), today.clone().endOf("week")]);
        break;
      case "Last Week": {
        const lastWeek = today.clone().subtract(1, "week");
        setRange([lastWeek.clone().startOf("week"), lastWeek.clone().endOf("week")]);
        break;
      }
      case "Custom":
        setRange(null);
        break;
      default:
        break;
    }
  };

  const handleSubmit = () => {
    if (!range) return;

    const startDate = range[0].clone().set({
      hour: startTime.hour(),
      minute: startTime.minute(),
      second: 0
    });

    const endDate = range[1].clone().set({
      hour: endTime.hour(),
      minute: endTime.minute(),
      second: 59
    });

    dispatch({
      type: "FEEDBACK_FILTER", data: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      }
    });
    setIsOpen(false);
  };

  const timerContent = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-evenly",
          alignItems: "center",
          marginTop: "5px"
        }}
      >
        <span style={{ color: "#19181A", fontWeight: 500 }}>From & End Time</span>
        <TimePicker
          use12Hours
          format="h:mm A"
          suffixIcon={null}
          value={startTime}
          allowClear={false}
          onChange={(value) => value && setStartTime(value)}
        />
        <ArrowRightOutlined />
        <TimePicker
          use12Hours
          format="h:mm A"
          suffixIcon={null}
          value={endTime}
          allowClear={false}
          onChange={(value) => value && setEndTime(value)}
        />
        <Button type="primary" onClick={handleSubmit} disabled={!range || !startDate || !endDate}>
          Apply
        </Button>
      </div>
    );
  };

  const content = (
    <div className="date-panel">
      <div className="date-side-panel">
        {["This Month", "Last Month", "This Week", "Last Week", "Custom"].map((opt) => (
          <p
            key={opt}
            onClick={() => handleOptionClick(opt)}
            style={{
              color: selectedOption === opt ? "#fff" : "#19181A",
              backgroundColor: selectedOption === opt ? "#1D7AFF" : "transparent",
            }}
          >
            {opt}
          </p>
        ))}
      </div>
      <div style={{ marginLeft: 24, position: "relative", zIndex: 0 }}>
        <RangePicker
          format="DD/MM/YYYY, HH:mm"
          value={range as any}
          open={isOpen}
          getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
          onChange={(dates) => {
            setSelectedOption("Custom");
            setRange(dates as [Moment, Moment]);
          }}
          transitionName=""
          renderExtraFooter={timerContent}
          style={{ width: 300 }}
        />
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomLeft"
      visible={isOpen}
      transitionName=""
      onVisibleChange={setIsOpen}
    >
      <div
        className="date-select"
        onClick={() => setIsOpen(!isOpen)}
      >
        <CalendarOutlined />
        <span>
          {selectedOption === "Custom" ? (
            <>
              {moment(startDate).format("MM/DD/YYYY, HH:mm")} 
              <SwapOutlined style={{ display: "inline-flex", transform: "rotateY(180deg)", padding: 8 }} /> 
              {moment(endDate).format("MM/DD/YYYY, HH:mm")}
            </>
          ) : (
            selectedOption
          )}
        </span>
        <svg
          width="16"
          height="9"
          viewBox="0 0 16 9"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0.999969 1L7.99997 8L15 1"
            stroke="#757575"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </Popover>
  );
};

export default CustomRangePicker;